"use client";

export const GOOGLE_CONSENT_STORAGE_KEY = "openings-google-consent-v2";

const GOOGLE_TAG_ID_RE = /^(?:G|GT|AW)-[A-Z0-9]+$/i;
const GOOGLE_ADS_ID_RE = /^AW-\d+$/i;
const GOOGLE_ADS_LABEL_RE = /^[A-Z0-9_-]+$/i;
const GOOGLE_TAG_MANAGER_ID_RE = /^GTM-[A-Z0-9]+$/i;
const GOOGLE_DEVELOPER_ID_RE = /^[A-Z0-9_-]+$/i;
const CONVERSION_EVENT_ID_RE = /^[A-Z0-9_-]{16,64}$/i;
const MANAGED_GOOGLE_ADS_DESTINATION_RE = /^AW-\d{1,20}\/[A-Za-z0-9_-]+$/;
const GOOGLE_TAG_SCRIPT_SELECTOR =
  'script[src^="https://www.googletagmanager.com/gtag/js"]';
const GOOGLE_TAG_MANAGER_SCRIPT_ATTRIBUTE = "data-openings-platform-gtm";
const BOOKING_DEDUPE_KEY_PREFIX = "openings-google-booking";
const MANAGED_CONVERSION_PENDING_KEY_PREFIX =
  "openings-google-managed-conversion-v1";
const MANAGED_PENDING_TTL_MS = 6 * 60 * 60 * 1_000;
const MAX_PENDING_STORAGE_LENGTH = 512;
const DEFAULT_EVENT_DELIVERY_TIMEOUT_MS = 1_500;

export const GOOGLE_MANAGED_CONVERSION_PENDING_CHANGE_EVENT =
  "openings:google-managed-conversion-pending-change";

export type GoogleConsentPreferences = {
  analytics: boolean;
  advertising: boolean;
};

export type StoredGoogleConsentPreferences = GoogleConsentPreferences & {
  version: 2;
};

export type OpeningsTrackingSurface = "booking_link" | "website";

export type GoogleTrackingConfigInput = {
  platformMeasurementId?: string | null;
  platformGtmId?: string | null;
  tenantGoogleTagId?: string | null;
  googleAdsConversionId?: string | null;
  googleAdsBookingConversionLabel?: string | null;
  developerId?: string | null;
  tenantId?: string | null;
  openingsSurface?: string | null;
};

export type ResolvedGoogleTrackingConfig = {
  platformMeasurementId: string | null;
  platformGtmId: string | null;
  tenantGoogleTagId: string | null;
  googleAdsConversionId: string | null;
  googleAdsConversionDestination: string | null;
  developerId: string | null;
  tenantId: string | null;
  openingsSurface: OpeningsTrackingSurface | null;
  cookieDomain: string;
  cookiePrefix: string;
};

export type BookingConversionInput = {
  /** Opaque, server-issued ID. Never pass an appointment/customer ID or PII. */
  conversionEventId: string;
  value: number | null;
  currency: string | null;
  /** Validated server response only; never derive this destination in-browser. */
  managedGoogleAds?: {
    eventId: string;
    destination: string;
  };
};

export type BookingConversionEvent = {
  name: "book_appointment" | "conversion";
  parameters: Record<string, unknown>;
};

type BookingConversionDispatchOptions = {
  eventCallback?: () => void;
  eventTimeoutMs?: number;
};

type ConsentBootstrap = {
  preferences: GoogleConsentPreferences;
  hasExplicitDecision: boolean;
  shouldPrompt: boolean;
};

type GoogleTagFunction = (...args: unknown[]) => void;

type GoogleTrackingRuntime = {
  config: ResolvedGoogleTrackingConfig;
  consent: GoogleConsentPreferences;
  suppressed: boolean;
  configuredTagIds: Set<string>;
  consentDefaultInitialized: boolean;
  globalSettingsInitialized: boolean;
  jsInitialized: boolean;
};

type NormalizedManagedGoogleAdsConversion = {
  eventId: string;
  destination: string;
};

type NormalizedBookingConversionInput = Omit<
  BookingConversionInput,
  "currency" | "managedGoogleAds"
> & {
  currency: string | null;
  managedGoogleAds?: NormalizedManagedGoogleAdsConversion;
};

type PendingManagedGoogleAdsConversion =
  NormalizedManagedGoogleAdsConversion & {
    version: 1;
    expiresAt: number;
  };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GoogleTagFunction;
    __OPENINGS_GOOGLE_TRACKING__?: GoogleTrackingRuntime;
  }
}

const DENIED_CONSENT: GoogleConsentPreferences = {
  analytics: false,
  advertising: false,
};

const dispatchedBookingConversions = new Set<string>();
const pendingManagedConversions = new Map<
  string,
  PendingManagedGoogleAdsConversion
>();
const pendingManagedConversionExpiryTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

function trimToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeOpeningsTrackingSurface(
  value: string | null | undefined,
): OpeningsTrackingSurface | null {
  return value === "booking_link" || value === "website" ? value : null;
}

export function normalizeGoogleTagId(
  value: string | null | undefined,
): string | null {
  const candidate = trimToNull(value)?.toUpperCase() ?? null;
  return candidate && GOOGLE_TAG_ID_RE.test(candidate) ? candidate : null;
}

export function normalizeGoogleAdsConversionId(
  value: string | null | undefined,
): string | null {
  const candidate = trimToNull(value)?.toUpperCase() ?? null;
  return candidate && GOOGLE_ADS_ID_RE.test(candidate) ? candidate : null;
}

export function buildGoogleAdsConversionDestination(
  conversionId: string | null | undefined,
  label: string | null | undefined,
): string | null {
  const normalizedId = normalizeGoogleAdsConversionId(conversionId);
  const normalizedLabel = trimToNull(label);

  if (
    !normalizedId ||
    !normalizedLabel ||
    !GOOGLE_ADS_LABEL_RE.test(normalizedLabel)
  ) {
    return null;
  }

  return `${normalizedId}/${normalizedLabel}`;
}

function normalizeGoogleTagManagerId(
  value: string | null | undefined,
): string | null {
  const candidate = trimToNull(value)?.toUpperCase() ?? null;
  return candidate && GOOGLE_TAG_MANAGER_ID_RE.test(candidate)
    ? candidate
    : null;
}

function normalizeGoogleDeveloperId(
  value: string | null | undefined,
): string | null {
  const candidate = trimToNull(value);
  return candidate && GOOGLE_DEVELOPER_ID_RE.test(candidate) ? candidate : null;
}

/** Stable, non-PII cookie namespace. The tenant ID is never put in the name. */
export function buildGoogleCookiePrefix(
  tenantId: string | null | undefined,
  hostname: string,
): string {
  const source = trimToNull(tenantId) ?? hostname.toLowerCase();
  let hash = 2166136261;

  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `op_${(hash >>> 0).toString(36)}`;
}

export function resolveGoogleTrackingConfig(
  input: GoogleTrackingConfigInput,
  hostname: string,
): ResolvedGoogleTrackingConfig {
  const googleAdsConversionId = normalizeGoogleAdsConversionId(
    input.googleAdsConversionId,
  );
  const googleAdsConversionDestination = buildGoogleAdsConversionDestination(
    googleAdsConversionId,
    input.googleAdsBookingConversionLabel,
  );

  return {
    platformMeasurementId: normalizeGoogleTagId(input.platformMeasurementId),
    platformGtmId: normalizeGoogleTagManagerId(input.platformGtmId),
    tenantGoogleTagId: normalizeGoogleTagId(input.tenantGoogleTagId),
    // A half-configured Ads action must fail closed: do not even initialize
    // the AW destination unless its conversion label is valid too.
    googleAdsConversionId: googleAdsConversionDestination
      ? googleAdsConversionId
      : null,
    googleAdsConversionDestination,
    developerId: normalizeGoogleDeveloperId(input.developerId),
    tenantId: trimToNull(input.tenantId),
    openingsSurface: normalizeOpeningsTrackingSurface(input.openingsSurface),
    cookieDomain: hostname,
    cookiePrefix: buildGoogleCookiePrefix(input.tenantId, hostname),
  };
}

export function hasConfiguredAdvertising(
  config: Pick<
    ResolvedGoogleTrackingConfig,
    "platformGtmId" | "tenantGoogleTagId" | "googleAdsConversionDestination"
  >,
): boolean {
  return (
    config.platformGtmId !== null ||
    config.tenantGoogleTagId?.startsWith("AW-") === true ||
    config.tenantGoogleTagId?.startsWith("GT-") === true ||
    config.googleAdsConversionDestination !== null
  );
}

export function hasAnyGoogleTracking(
  config: ResolvedGoogleTrackingConfig,
): boolean {
  return Boolean(
    config.platformMeasurementId ||
    config.platformGtmId ||
    config.tenantGoogleTagId ||
    config.googleAdsConversionDestination,
  );
}

export function resolveConsentBootstrap({
  storedPreferences,
  effectiveConsentRequired,
  defaultAdvertisingConsent = false,
}: {
  storedPreferences: GoogleConsentPreferences | null;
  effectiveConsentRequired: boolean;
  /**
   * Tenant opt-in that pre-grants advertising storage. It is deliberately
   * ignored whenever consent is required: a displayed prompt owns the first
   * decision, and a stored visitor choice always outranks this default.
   */
  defaultAdvertisingConsent?: boolean;
}): ConsentBootstrap {
  if (storedPreferences) {
    return {
      preferences: storedPreferences,
      hasExplicitDecision: true,
      shouldPrompt: false,
    };
  }

  if (effectiveConsentRequired) {
    return {
      preferences: DENIED_CONSENT,
      hasExplicitDecision: false,
      shouldPrompt: true,
    };
  }

  return {
    // Analytics-only sites preserve their existing visit measurement without
    // silently granting advertising signals. AW-/GT-/GTM configurations make
    // consent explicit before this branch can be reached, so the tenant
    // opt-in below can only ever apply where no prompt is owed.
    preferences: { analytics: true, advertising: defaultAdvertisingConsent },
    hasExplicitDecision: false,
    shouldPrompt: false,
  };
}

type ConsentStorage = Pick<Storage, "getItem" | "setItem">;

export function getBrowserLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    // Opaque origins, sandboxed embeds, and strict browser policies can throw
    // while accessing the storage property itself.
    return null;
  }
}

function getBrowserSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    // Same policy as localStorage: keep a bounded in-memory fallback when a
    // browser denies access to the same-tab store.
    return null;
  }
}

function isGoogleStorageNamespace(namespace: string): boolean {
  return /^op_[a-z0-9]+$/.test(namespace);
}

function managedConversionPendingKey(
  config: Pick<ResolvedGoogleTrackingConfig, "cookiePrefix">,
): string {
  return `${MANAGED_CONVERSION_PENDING_KEY_PREFIX}:${config.cookiePrefix}`;
}

function removeSessionStorageValue(key: string): void {
  try {
    getBrowserSessionStorage()?.removeItem(key);
  } catch {
    // The in-memory state is still cleared by each caller.
  }
}

function notifyManagedConversionPendingChange(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new Event(GOOGLE_MANAGED_CONVERSION_PENDING_CHANGE_EVENT),
  );
}

function clearPendingManagedConversionExpiryTimer(key: string): void {
  const timer = pendingManagedConversionExpiryTimers.get(key);
  if (timer) clearTimeout(timer);
  pendingManagedConversionExpiryTimers.delete(key);
}

function schedulePendingManagedConversionExpiry(
  config: Pick<ResolvedGoogleTrackingConfig, "cookiePrefix">,
  pending: PendingManagedGoogleAdsConversion,
  now: number,
): void {
  if (typeof window === "undefined") return;
  const key = managedConversionPendingKey(config);
  clearPendingManagedConversionExpiryTimer(key);
  const timer = setTimeout(
    () => {
      pendingManagedConversionExpiryTimers.delete(key);
      const current = pendingManagedConversions.get(key);
      if (
        current &&
        (current.eventId !== pending.eventId ||
          current.destination !== pending.destination ||
          current.expiresAt !== pending.expiresAt)
      ) {
        return;
      }
      pendingManagedConversions.delete(key);
      removeSessionStorageValue(key);
      notifyManagedConversionPendingChange();
    },
    Math.max(0, pending.expiresAt - now),
  );
  pendingManagedConversionExpiryTimers.set(key, timer);
}

function isPendingManagedConversion(
  value: unknown,
  now: number,
): value is PendingManagedGoogleAdsConversion {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).sort().join("\0") !==
    ["destination", "eventId", "expiresAt", "version"].join("\0")
  ) {
    return false;
  }
  return (
    record.version === 1 &&
    typeof record.eventId === "string" &&
    CONVERSION_EVENT_ID_RE.test(record.eventId) &&
    typeof record.destination === "string" &&
    MANAGED_GOOGLE_ADS_DESTINATION_RE.test(record.destination) &&
    typeof record.expiresAt === "number" &&
    Number.isSafeInteger(record.expiresAt) &&
    record.expiresAt > now &&
    record.expiresAt <= now + MANAGED_PENDING_TTL_MS
  );
}

function readPendingManagedGoogleAdsConversion(
  config: Pick<
    ResolvedGoogleTrackingConfig,
    "cookiePrefix" | "developerId" | "tenantId" | "openingsSurface"
  >,
  now = Date.now(),
): PendingManagedGoogleAdsConversion | null {
  if (
    !Number.isSafeInteger(now) ||
    !config.tenantId ||
    !config.openingsSurface ||
    !config.developerId ||
    !isGoogleStorageNamespace(config.cookiePrefix)
  ) {
    return null;
  }

  const key = managedConversionPendingKey(config);
  const memoryValue = pendingManagedConversions.get(key);
  if (memoryValue) {
    if (isPendingManagedConversion(memoryValue, now)) {
      schedulePendingManagedConversionExpiry(config, memoryValue, now);
      return memoryValue;
    }
    pendingManagedConversions.delete(key);
    clearPendingManagedConversionExpiryTimer(key);
  }

  const storage = getBrowserSessionStorage();
  if (!storage) return null;
  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    if (serialized.length > MAX_PENDING_STORAGE_LENGTH) {
      storage.removeItem(key);
      clearPendingManagedConversionExpiryTimer(key);
      return null;
    }
    const parsed: unknown = JSON.parse(serialized);
    if (!isPendingManagedConversion(parsed, now)) {
      storage.removeItem(key);
      clearPendingManagedConversionExpiryTimer(key);
      return null;
    }
    pendingManagedConversions.set(key, parsed);
    schedulePendingManagedConversionExpiry(config, parsed, now);
    return parsed;
  } catch {
    clearPendingManagedConversionExpiryTimer(key);
    removeSessionStorageValue(key);
    return null;
  }
}

function queuePendingManagedGoogleAdsConversion(
  config: Pick<
    ResolvedGoogleTrackingConfig,
    "cookiePrefix" | "developerId" | "tenantId" | "openingsSurface"
  >,
  conversion: NormalizedManagedGoogleAdsConversion,
  now = Date.now(),
): boolean {
  if (
    !Number.isSafeInteger(now) ||
    !config.tenantId ||
    !config.openingsSurface ||
    !config.developerId ||
    !isGoogleStorageNamespace(config.cookiePrefix)
  ) {
    return false;
  }
  const key = managedConversionPendingKey(config);
  const existing = readPendingManagedGoogleAdsConversion(config, now);
  if (
    existing?.eventId === conversion.eventId &&
    existing.destination === conversion.destination
  ) {
    return true;
  }
  const pending: PendingManagedGoogleAdsConversion = {
    version: 1,
    eventId: conversion.eventId,
    destination: conversion.destination,
    expiresAt: now + MANAGED_PENDING_TTL_MS,
  };
  pendingManagedConversions.set(key, pending);
  schedulePendingManagedConversionExpiry(config, pending, now);
  try {
    getBrowserSessionStorage()?.setItem(key, JSON.stringify(pending));
  } catch {
    // The bounded in-memory item still supports late consent in this document.
  }
  notifyManagedConversionPendingChange();
  return true;
}

function clearPendingManagedGoogleAdsConversion(
  config: Pick<ResolvedGoogleTrackingConfig, "cookiePrefix">,
  expected?: NormalizedManagedGoogleAdsConversion,
): void {
  if (!isGoogleStorageNamespace(config.cookiePrefix)) return;
  const key = managedConversionPendingKey(config);
  if (expected) {
    const current = pendingManagedConversions.get(key);
    if (
      current &&
      (current.eventId !== expected.eventId ||
        current.destination !== expected.destination)
    ) {
      return;
    }
  }
  pendingManagedConversions.delete(key);
  clearPendingManagedConversionExpiryTimer(key);
  removeSessionStorageValue(key);
  notifyManagedConversionPendingChange();
}

export function hasPendingManagedGoogleAdsConversion(
  config: Pick<
    ResolvedGoogleTrackingConfig,
    "cookiePrefix" | "developerId" | "tenantId" | "openingsSurface"
  >,
  now = Date.now(),
): boolean {
  return readPendingManagedGoogleAdsConversion(config, now) !== null;
}

export function buildGoogleConsentStorageKey(namespace: string): string {
  return `${GOOGLE_CONSENT_STORAGE_KEY}:${namespace}`;
}

export function readStoredGoogleConsent(
  storage: ConsentStorage | null,
  namespace: string,
): GoogleConsentPreferences | null {
  if (!storage) return null;

  try {
    const serialized = storage.getItem(buildGoogleConsentStorageKey(namespace));
    if (serialized) {
      const parsed = JSON.parse(
        serialized,
      ) as Partial<StoredGoogleConsentPreferences>;
      if (
        parsed.version === 2 &&
        typeof parsed.analytics === "boolean" &&
        typeof parsed.advertising === "boolean"
      ) {
        return {
          analytics: parsed.analytics,
          advertising: parsed.advertising,
        };
      }
    }

    // Do not migrate the old origin-wide consent keys. A hostname can be
    // reassigned to another tenant, so only this tenant-scoped decision is
    // authoritative.
  } catch {
    return null;
  }

  return null;
}

export function writeStoredGoogleConsent(
  storage: ConsentStorage | null,
  preferences: GoogleConsentPreferences,
  namespace: string,
): void {
  if (!storage) return;

  try {
    const stored: StoredGoogleConsentPreferences = {
      version: 2,
      analytics: preferences.analytics,
      advertising: preferences.advertising,
    };
    storage.setItem(
      buildGoogleConsentStorageKey(namespace),
      JSON.stringify(stored),
    );
  } catch {
    // Private browsing and strict storage policies can reject localStorage.
    // The in-memory decision still applies for the current page.
  }
}

export function shouldSuppressGoogleTracking({
  pathname,
  search = "",
  embedded,
}: {
  pathname: string;
  search?: string;
  embedded: boolean;
}): boolean {
  const sensitivePathPrefixes = [
    "/appointment/review",
    "/appointment/feedback",
    "/feedback",
    "/manage",
    "/quote",
    "/requests",
  ];
  const isSensitivePath = sensitivePathPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  let hasSensitiveQuery = false;
  try {
    const searchParams = new URLSearchParams(search);
    const legacyView = searchParams.get("v")?.toLowerCase();
    hasSensitiveQuery =
      legacyView === "m" ||
      legacyView === "q" ||
      legacyView === "f" ||
      [
        "appointmentId",
        "customerId",
        "quoteId",
        "quotePublicId",
        "requestId",
        "token",
        "verificationCode",
      ].some((key) => searchParams.has(key));
  } catch {
    // Malformed search strings fail closed.
    hasSensitiveQuery = true;
  }

  return (
    embedded ||
    isSensitivePath ||
    hasSensitiveQuery ||
    pathname === "/dev" ||
    pathname.startsWith("/dev/")
  );
}

function currentBrowserContextIsSuppressed(): boolean {
  if (typeof window === "undefined") return true;

  let embedded = false;
  try {
    embedded = window.self !== window.top;
  } catch {
    embedded = true;
  }

  return shouldSuppressGoogleTracking({
    pathname: window.location.pathname,
    search: window.location.search,
    embedded,
  });
}

export function buildGoogleConsentParameters(
  preferences: GoogleConsentPreferences,
): Record<string, "granted" | "denied"> {
  return {
    analytics_storage: preferences.analytics ? "granted" : "denied",
    ad_storage: preferences.advertising ? "granted" : "denied",
    ad_user_data: preferences.advertising ? "granted" : "denied",
    // V1 supports conversion measurement only. It never enables remarketing
    // or personalized advertising, even when the visitor allows Ads cookies.
    ad_personalization: "denied",
  };
}

export function canLoadMixedGoogleContainer(
  preferences: GoogleConsentPreferences,
): boolean {
  return preferences.analytics && preferences.advertising;
}

function ensureGoogleTagFunction(): GoogleTagFunction | null {
  if (typeof window === "undefined") return null;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // Google explicitly consumes the function's array-like Arguments object.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
  }

  return window.gtag;
}

function ensureGoogleTagScript(primaryTagId: string): void {
  if (typeof document === "undefined") return;
  if (document.querySelector(GOOGLE_TAG_SCRIPT_SELECTOR)) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    primaryTagId,
  )}`;
  script.dataset.openingsGoogleTag = "true";
  document.head.appendChild(script);
}

function ensurePlatformGoogleTagManager(gtmId: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const alreadyLoaded = Array.from(document.scripts).some((candidate) => {
    try {
      const source = new URL(candidate.src);
      return (
        source.hostname === "www.googletagmanager.com" &&
        source.pathname === "/gtm.js" &&
        source.searchParams.get("id")?.toUpperCase() === gtmId
      );
    } catch {
      return false;
    }
  });
  if (
    alreadyLoaded ||
    document.querySelector(
      `script[${GOOGLE_TAG_MANAGER_SCRIPT_ATTRIBUTE}="${gtmId}"]`,
    )
  ) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    gtmId,
  )}`;
  script.setAttribute(GOOGLE_TAG_MANAGER_SCRIPT_ATTRIBUTE, gtmId);
  document.head.appendChild(script);
}

function tagCanRunWithConsent(
  tagId: string,
  consent: GoogleConsentPreferences,
): boolean {
  if (tagId.startsWith("AW-")) return consent.advertising;
  if (tagId.startsWith("G-")) return consent.analytics;
  // A GT- Google tag can have Analytics and Ads destinations that the site
  // cannot inspect. Basic consent mode therefore loads it only after both
  // optional categories are granted.
  return consent.analytics && consent.advertising;
}

function allowedTagIds(
  config: ResolvedGoogleTrackingConfig,
  consent: GoogleConsentPreferences,
): string[] {
  return [
    config.platformMeasurementId,
    config.tenantGoogleTagId,
    consent.advertising ? config.googleAdsConversionId : null,
  ].filter(
    (tagId, index, all): tagId is string =>
      Boolean(tagId && tagCanRunWithConsent(tagId, consent)) &&
      all.indexOf(tagId) === index,
  );
}

export function buildGoogleTagConfigParameters(
  config: ResolvedGoogleTrackingConfig,
  tagId: string,
): Record<string, unknown> {
  const parameters: Record<string, unknown> = {
    cookie_domain: config.cookieDomain,
    cookie_prefix: config.cookiePrefix,
    // Openings never supports enhanced conversions or automatic
    // user-provided-data collection. Keep this boundary at config scope too.
    user_data: {},
  };

  const isTenantDestination =
    tagId === config.tenantGoogleTagId ||
    tagId === config.googleAdsConversionId;
  if (isTenantDestination && config.openingsSurface) {
    parameters.openings_surface = config.openingsSurface;
  }

  if (tagId === config.platformMeasurementId && config.tenantId) {
    parameters.user_properties = { tenant_id: config.tenantId };
  }

  return parameters;
}

/**
 * Initializes or reactively updates the browser runtime. Google scripts are
 * not requested until at least one applicable consent category is granted.
 */
export function applyGoogleTracking(
  config: ResolvedGoogleTrackingConfig,
  consent: GoogleConsentPreferences,
  suppressed: boolean,
): void {
  if (typeof window === "undefined") return;

  const existing = window.__OPENINGS_GOOGLE_TRACKING__;
  const runtime: GoogleTrackingRuntime = existing ?? {
    config,
    consent,
    suppressed,
    configuredTagIds: new Set<string>(),
    consentDefaultInitialized: false,
    globalSettingsInitialized: false,
    jsInitialized: false,
  };

  runtime.config = config;
  runtime.consent = consent;
  runtime.suppressed = suppressed;
  window.__OPENINGS_GOOGLE_TRACKING__ = runtime;

  if (suppressed) {
    if (existing && window.gtag) {
      window.gtag(
        "consent",
        "update",
        buildGoogleConsentParameters(DENIED_CONSENT),
      );
    }
    return;
  }

  const gtag = ensureGoogleTagFunction();
  if (!gtag) return;

  if (!runtime.consentDefaultInitialized) {
    gtag("consent", "default", {
      ...buildGoogleConsentParameters(DENIED_CONSENT),
      wait_for_update: 500,
    });
    runtime.consentDefaultInitialized = true;
  }

  if (!runtime.globalSettingsInitialized) {
    if (config.developerId) {
      gtag("set", `developer_id.${config.developerId}`, true);
    }
    gtag("set", {
      cookie_domain: config.cookieDomain,
      cookie_prefix: config.cookiePrefix,
      // Openings never supports enhanced conversions or automatic
      // user-provided-data collection. An explicit empty value prevents a
      // tenant's remote Google tag settings from scraping booking-form PII.
      user_data: {},
    });
    runtime.globalSettingsInitialized = true;
  }

  if (!runtime.jsInitialized) {
    gtag("js", new Date());
    runtime.jsInitialized = true;
  }

  gtag("consent", "update", buildGoogleConsentParameters(consent));

  const tagIds = allowedTagIds(config, consent);
  for (const tagId of tagIds) {
    if (runtime.configuredTagIds.has(tagId)) continue;

    gtag("config", tagId, buildGoogleTagConfigParameters(config, tagId));
    runtime.configuredTagIds.add(tagId);
  }

  if (tagIds[0]) {
    ensureGoogleTagScript(tagIds[0]);
  }

  if (
    config.platformGtmId &&
    // The platform container can hold multiple tag categories. Loading it on
    // a partial grant would weaken basic-mode blocking for the denied category.
    canLoadMixedGoogleContainer(consent)
  ) {
    ensurePlatformGoogleTagManager(config.platformGtmId);
  }

  // Consent Mode is updated and every static destination is configured before
  // a previously validated managed event is allowed to leave same-tab storage.
  flushPendingManagedGoogleAdsConversion();
}

function normalizedBookingConversionInput(
  input: BookingConversionInput,
): NormalizedBookingConversionInput | null {
  const conversionEventId = trimToNull(input.conversionEventId);
  const currency = trimToNull(input.currency)?.toUpperCase() ?? null;

  if (
    !conversionEventId ||
    !CONVERSION_EVENT_ID_RE.test(conversionEventId) ||
    (input.value !== null &&
      (!Number.isFinite(input.value) || input.value < 0)) ||
    (input.value !== null && (!currency || !/^[A-Z]{3}$/.test(currency)))
  ) {
    return null;
  }

  const rawManagedEventId = input.managedGoogleAds?.eventId;
  const rawManagedDestination = input.managedGoogleAds?.destination;
  const managedEventId = trimToNull(rawManagedEventId);
  const managedDestination = trimToNull(rawManagedDestination);
  const managedGoogleAds =
    managedEventId &&
    managedEventId === rawManagedEventId &&
    CONVERSION_EVENT_ID_RE.test(managedEventId) &&
    managedDestination &&
    managedDestination === rawManagedDestination &&
    managedDestination.length <= 256 &&
    MANAGED_GOOGLE_ADS_DESTINATION_RE.test(managedDestination)
      ? { eventId: managedEventId, destination: managedDestination }
      : null;

  return {
    conversionEventId,
    value: input.value,
    currency: input.value === null ? null : currency,
    ...(managedGoogleAds ? { managedGoogleAds } : {}),
  };
}

function withDeveloperId(
  parameters: Record<string, unknown>,
  developerId: string | null,
): Record<string, unknown> {
  if (!developerId) return parameters;
  return { ...parameters, [`developer_id.${developerId}`]: true };
}

function buildManagedGoogleAdsConversionEvent(
  conversion: NormalizedManagedGoogleAdsConversion,
  config: ResolvedGoogleTrackingConfig,
): BookingConversionEvent {
  const parameters: Record<string, unknown> = {
    transaction_id: conversion.eventId,
    send_to: conversion.destination,
    // Managed Autopilot optimizes booking count/CPA, not revenue. Keeping the
    // event to its opaque ID and stored destination also makes late-consent
    // delivery identical to immediate delivery.
    user_data: {},
  };
  if (config.openingsSurface) {
    parameters.openings_surface = config.openingsSurface;
  }
  return {
    name: "conversion",
    parameters: withDeveloperId(parameters, config.developerId),
  };
}

export function buildBookingConversionEvents({
  input,
  config,
  consent,
  suppressed,
  eventCallback,
  eventTimeoutMs = DEFAULT_EVENT_DELIVERY_TIMEOUT_MS,
}: {
  input: BookingConversionInput;
  config: ResolvedGoogleTrackingConfig;
  consent: GoogleConsentPreferences;
  suppressed: boolean;
  eventCallback?: () => void;
  eventTimeoutMs?: number;
}): BookingConversionEvent[] {
  const normalizedInput = normalizedBookingConversionInput(input);
  if (suppressed || !normalizedInput) return [];

  const commonParameters: Record<string, unknown> = {
    transaction_id: normalizedInput.conversionEventId,
    // Event scope has the highest gtag parameter precedence. Keep this empty
    // even if the tenant enables automatic enhanced conversions remotely.
    user_data: {},
  };
  if (config.openingsSurface) {
    commonParameters.openings_surface = config.openingsSurface;
  }
  if (normalizedInput.value !== null && normalizedInput.currency) {
    commonParameters.value = normalizedInput.value;
    commonParameters.currency = normalizedInput.currency;
  }
  const events: BookingConversionEvent[] = [];

  if (
    config.tenantGoogleTagId &&
    tagCanRunWithConsent(config.tenantGoogleTagId, consent)
  ) {
    events.push({
      name: "book_appointment",
      parameters: withDeveloperId(
        {
          ...commonParameters,
          send_to: config.tenantGoogleTagId,
        },
        config.developerId,
      ),
    });
  }

  if (consent.advertising && config.googleAdsConversionDestination) {
    events.push({
      name: "conversion",
      parameters: withDeveloperId(
        {
          ...commonParameters,
          send_to: config.googleAdsConversionDestination,
        },
        config.developerId,
      ),
    });
  }

  if (
    consent.advertising &&
    config.developerId &&
    config.tenantId &&
    config.openingsSurface &&
    normalizedInput.managedGoogleAds
  ) {
    events.push(
      buildManagedGoogleAdsConversionEvent(
        normalizedInput.managedGoogleAds,
        config,
      ),
    );
  }

  const finalEvent = events.at(-1);
  if (finalEvent && eventCallback) {
    finalEvent.parameters.event_callback = eventCallback;
    finalEvent.parameters.event_timeout = Math.max(
      0,
      Math.min(eventTimeoutMs, 5_000),
    );
  }

  return events;
}

function bookingDedupeKey(
  config: ResolvedGoogleTrackingConfig,
  event: BookingConversionEvent,
): string | null {
  const destination = event.parameters.send_to;
  const conversionEventId = event.parameters.transaction_id;
  if (
    typeof destination !== "string" ||
    destination.length === 0 ||
    typeof conversionEventId !== "string" ||
    !CONVERSION_EVENT_ID_RE.test(conversionEventId)
  ) {
    return null;
  }
  // Exact destination text is non-PII and avoids a short-hash collision
  // turning one tenant event into another event's false duplicate.
  return `${BOOKING_DEDUPE_KEY_PREFIX}:${config.cookiePrefix}:${event.name}:${encodeURIComponent(
    destination,
  )}:${conversionEventId}`;
}

function hasDispatchedBookingConversion(key: string): boolean {
  if (dispatchedBookingConversions.has(key)) return true;

  try {
    if (window.sessionStorage.getItem(key) === "sent") {
      dispatchedBookingConversions.add(key);
      return true;
    }
  } catch {
    // In-memory dedupe remains available.
  }

  return false;
}

function markBookingConversionDispatched(key: string): void {
  dispatchedBookingConversions.add(key);
  try {
    window.sessionStorage.setItem(key, "sent");
  } catch {
    // In-memory dedupe remains available.
  }
}

function ensureManagedGoogleAdsDestination(
  runtime: GoogleTrackingRuntime,
  conversion: NormalizedManagedGoogleAdsConversion,
  gtag: GoogleTagFunction,
): boolean {
  const { config } = runtime;
  if (
    runtime.suppressed ||
    !runtime.consent.advertising ||
    !config.developerId ||
    !config.tenantId ||
    !config.openingsSurface ||
    conversion.destination.length > 256 ||
    !MANAGED_GOOGLE_ADS_DESTINATION_RE.test(conversion.destination)
  ) {
    return false;
  }

  const slashIndex = conversion.destination.indexOf("/");
  const baseId = conversion.destination.slice(0, slashIndex);
  if (!GOOGLE_ADS_ID_RE.test(baseId)) return false;

  if (!runtime.configuredTagIds.has(baseId)) {
    gtag(
      "config",
      baseId,
      withDeveloperId(
        {
          cookie_domain: config.cookieDomain,
          cookie_prefix: config.cookiePrefix,
          openings_surface: config.openingsSurface,
          user_data: {},
        },
        config.developerId,
      ),
    );
    runtime.configuredTagIds.add(baseId);
  }
  ensureGoogleTagScript(baseId);
  return true;
}

type BookingConversionDispatchCandidate = {
  event: BookingConversionEvent;
  managedGoogleAds?: NormalizedManagedGoogleAdsConversion;
};

function dispatchBookingConversionCandidates(
  runtime: GoogleTrackingRuntime,
  candidates: BookingConversionDispatchCandidate[],
  options: BookingConversionDispatchOptions = {},
): boolean {
  const gtag = ensureGoogleTagFunction();
  if (!gtag) return false;

  const pending: Array<
    BookingConversionDispatchCandidate & { dedupeKey: string }
  > = [];
  for (const candidate of candidates) {
    const dedupeKey = bookingDedupeKey(runtime.config, candidate.event);
    if (!dedupeKey) continue;
    if (hasDispatchedBookingConversion(dedupeKey)) {
      if (candidate.managedGoogleAds) {
        clearPendingManagedGoogleAdsConversion(
          runtime.config,
          candidate.managedGoogleAds,
        );
      }
      continue;
    }
    if (
      candidate.managedGoogleAds &&
      !ensureManagedGoogleAdsDestination(
        runtime,
        candidate.managedGoogleAds,
        gtag,
      )
    ) {
      continue;
    }
    pending.push({ ...candidate, dedupeKey });
  }
  if (pending.length === 0) return false;

  const finalIndex = pending.length - 1;
  for (const [index, candidate] of pending.entries()) {
    const parameters = { ...candidate.event.parameters };
    if (index === finalIndex && options.eventCallback) {
      parameters.event_callback = options.eventCallback;
      parameters.event_timeout = Math.max(
        0,
        Math.min(
          options.eventTimeoutMs ?? DEFAULT_EVENT_DELIVERY_TIMEOUT_MS,
          5_000,
        ),
      );
    }
    gtag("event", candidate.event.name, parameters);
    markBookingConversionDispatched(candidate.dedupeKey);
    if (candidate.managedGoogleAds) {
      clearPendingManagedGoogleAdsConversion(
        runtime.config,
        candidate.managedGoogleAds,
      );
    }
  }
  return true;
}

export function flushPendingManagedGoogleAdsConversion(): boolean {
  if (typeof window === "undefined" || currentBrowserContextIsSuppressed()) {
    return false;
  }
  const runtime = window.__OPENINGS_GOOGLE_TRACKING__;
  if (!runtime || runtime.suppressed || !runtime.consent.advertising) {
    return false;
  }
  const pending = readPendingManagedGoogleAdsConversion(runtime.config);
  if (!pending) return false;
  return dispatchBookingConversionCandidates(runtime, [
    {
      event: buildManagedGoogleAdsConversionEvent(pending, runtime.config),
      managedGoogleAds: pending,
    },
  ]);
}

/**
 * Sends a completed-booking event using the initialized tenant configuration.
 * This helper is intentionally independent of React and accepts no customer or
 * appointment fields, so callers cannot accidentally add PII.
 */
export function trackBookingConversion(
  input: BookingConversionInput,
  options: BookingConversionDispatchOptions = {},
): boolean {
  if (typeof window === "undefined") return false;

  const runtime = window.__OPENINGS_GOOGLE_TRACKING__;
  if (!runtime || runtime.suppressed || currentBrowserContextIsSuppressed()) {
    return false;
  }

  const normalizedInput = normalizedBookingConversionInput(input);
  if (!normalizedInput) return false;

  if (
    normalizedInput.managedGoogleAds &&
    !runtime.consent.advertising &&
    runtime.config.developerId
  ) {
    queuePendingManagedGoogleAdsConversion(
      runtime.config,
      normalizedInput.managedGoogleAds,
    );
  }

  const events = buildBookingConversionEvents({
    input: normalizedInput,
    config: runtime.config,
    consent: runtime.consent,
    suppressed: false,
  });
  if (events.length === 0) return false;

  return dispatchBookingConversionCandidates(
    runtime,
    events.map((event) => {
      const isManaged =
        normalizedInput.managedGoogleAds &&
        event.name === "conversion" &&
        event.parameters.send_to ===
          normalizedInput.managedGoogleAds.destination &&
        event.parameters.transaction_id ===
          normalizedInput.managedGoogleAds.eventId;
      return {
        event,
        ...(isManaged
          ? { managedGoogleAds: normalizedInput.managedGoogleAds }
          : {}),
      };
    }),
    options,
  );
}

/**
 * Queue a conversion and wait for Google's delivery callback before a hard
 * navigation. A local fallback guarantees analytics can never trap the user
 * on a completed-booking screen if the tag or network stalls.
 */
export function trackBookingConversionAndWait(
  input: BookingConversionInput,
  eventTimeoutMs = DEFAULT_EVENT_DELIVERY_TIMEOUT_MS,
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const complete = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      resolve(true);
    };

    const dispatched = trackBookingConversion(input, {
      eventCallback: complete,
      eventTimeoutMs,
    });
    if (!dispatched) {
      settled = true;
      resolve(false);
      return;
    }

    if (!settled) {
      fallbackTimer = setTimeout(complete, eventTimeoutMs + 100);
    }
  });
}
